import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  Signal,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Assistant, ConfigVoiceConfig } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs';
import { AvailableModelsService } from '../available-models.service';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { capitalizeFirst, clampIterations, formatVoiceConfig, LLMOptionPair } from '../test-run.utils';
import { AiVoiceConfigurationForm } from '../../../core/forms/ai-voice-model-config.form';
import { AiVoiceConfigurationFormComponent } from '../../ai-voice-configuration-form/ai-voice-configuration-form.component';

interface VoiceSummary {
  familyName: string;
  voiceName?: string;
}

type VoiceConfigMode = 'active' | 'override';

interface VoiceConfigEntry {
  form: AiVoiceConfigurationForm;
  summary: Signal<VoiceSummary>;
  valid: Signal<boolean>;
  mode: WritableSignal<VoiceConfigMode>;
}

interface RunPreview {
  model: string;
  modelName: string;
  reasoningEffort?: string;
  name: string;
  isEditing: boolean;
  voiceSummary?: VoiceSummary;
}

export interface TestRunOptionsModalResult {
  llmOptions: LLMOptionPair[];
  voiceConfigs: ConfigVoiceConfig[];
  iterations: number;
  runNames: string[];
}

export interface TestRunOptionsModalData {
  assistant?: Assistant;
  voiceOnly?: boolean;
}

interface ModelSelection {
  id: string;
  name: string;
  isDefault: boolean;
  supportedReasoningEfforts: string[];
  selected: boolean;
  // Changed from single string to array to support multiple selections
  selectedReasoningEfforts: string[];
}

@Component({
  selector: 'ai-test-run-options-modal',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyBadgeModule,
    GalaxyButtonGroupModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTooltipModule,
    AiAssistantI18nModule,
    AiVoiceConfigurationFormComponent,
  ],

  templateUrl: './test-run-options-modal.component.html',
  styleUrl: './test-run-options-modal.component.scss',
})
export class TestRunOptionsModalComponent {
  private readonly dialogRef = inject(MatDialogRef<TestRunOptionsModalComponent, TestRunOptionsModalResult>);
  private readonly availableModelsService = inject(AvailableModelsService);
  private readonly translateService = inject(TranslateService);
  protected readonly data = inject<TestRunOptionsModalData>(MAT_DIALOG_DATA, { optional: true });
  readonly voiceOnly = this.data?.voiceOnly ?? false;
  readonly activeVoiceLabel = formatVoiceConfig(this.data?.assistant?.config?.voiceConfig);

  private get assistantDefaultModel(): string | undefined {
    return this.data?.assistant?.config?.models?.[0]?.id;
  }

  // Signal to track model selections
  modelSelections = signal<ModelSelection[]>([]);

  iterations = signal<number>(5);

  private readonly rawModels = toSignal(
    this.availableModelsService.getModels().pipe(
      map((modelsList) => {
        let assistantModelId = this.assistantDefaultModel;

        // If no assistantModelId, use the recommended model (this is the model the assistant uses if none is selected)
        if (!assistantModelId) {
          const recommendedModel = modelsList.find((model) => model.recommended);
          if (recommendedModel?.id) {
            assistantModelId = recommendedModel.id;
          }
        }

        const selections: ModelSelection[] = modelsList.map((model) => {
          const modelId = model.id || '';
          const isDefault = !!(assistantModelId && modelId === assistantModelId);
          const supportedEfforts = model.supportedReasoningEfforts || [];

          return {
            id: modelId,
            name: model.name || modelId,
            isDefault,
            supportedReasoningEfforts: supportedEfforts,
            // Pre-select the default model
            selected: isDefault,
            // Pre-select the first reasoning effort if model supports it
            selectedReasoningEfforts: supportedEfforts.length > 0 ? [supportedEfforts[0]] : [],
          };
        });

        return selections;
      }),
    ),
  );

  readonly voiceConfigEntries = signal<VoiceConfigEntry[]>([]);
  private readonly injector = inject(Injector);

  constructor() {
    effect(() => {
      const models = this.rawModels();
      if (models && models.length > 0) {
        untracked(() => {
          this.modelSelections.set(models);
          this.buildRunPreviews();
        });
      }
    });

    effect(() => {
      if (!this.voiceOnly) return;
      const entries = this.voiceConfigEntries();
      const contextName =
        this.data?.assistant?.name ||
        this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.RUN_NAME_FALLBACK');
      const activeSuffix = this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.ACTIVE_VOICE_SUFFIX');
      const voiceFallback = this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.VOICE_FAMILY_FALLBACK');
      const previews: RunPreview[] = entries.map((entry) => {
        if (entry.mode() === 'active') {
          return { model: '', modelName: '', name: `${contextName} - ${activeSuffix}`, isEditing: false };
        }
        const summary = entry.summary();
        const voiceLabel = summary.voiceName
          ? `${summary.familyName}/${summary.voiceName}`
          : summary.familyName || voiceFallback;
        return {
          model: '',
          modelName: '',
          name: `${contextName} - ${voiceLabel}`,
          isEditing: false,
          voiceSummary: summary,
        };
      });
      untracked(() => this.runPreviews.set(previews));
    });

    if (this.voiceOnly) {
      this.voiceConfigEntries.set([this.createVoiceEntry(this.seedVoiceConfig(), 'active')]);
    }
  }

  addVoiceConfig(): void {
    this.voiceConfigEntries.update((entries) => [
      ...entries,
      this.createVoiceEntry(this.seedVoiceConfig(), 'override'),
    ]);
  }

  removeVoiceConfig(form: AiVoiceConfigurationForm): void {
    this.voiceConfigEntries.update((entries) =>
      entries.length > 1 ? entries.filter((e) => e.form !== form) : entries,
    );
  }

  setEntryMode(entry: VoiceConfigEntry, mode: VoiceConfigMode): void {
    entry.mode.set(mode);
  }

  private seedVoiceConfig(): ConfigVoiceConfig | undefined {
    return this.data?.assistant?.config?.voiceConfig;
  }

  private createVoiceEntry(
    seed: ConfigVoiceConfig | undefined,
    mode: VoiceConfigMode,
  ): VoiceConfigEntry {
    const form = new AiVoiceConfigurationForm(seed);
    const initial: VoiceSummary = { familyName: '', voiceName: undefined };
    const summary = toSignal(form.voiceSummary$.pipe(map((s): VoiceSummary => s ?? initial)), {
      initialValue: initial,
      injector: this.injector,
    });
    const valid = toSignal(form.statusChanges.pipe(startWith(form.status), map(() => form.valid)), {
      initialValue: form.valid,
      injector: this.injector,
    });
    return { form, summary, valid, mode: signal(mode) };
  }

  readonly voiceConfigsValid = computed(() =>
    this.voiceConfigEntries().every((e) => e.mode() === 'active' || e.valid()),
  );

  readonly modelsLoading = computed(() => this.rawModels() === undefined);

  readonly hasSelection = computed(() => {
    return this.modelSelections().some((m) => m.selected);
  });

  /**
   * Count of total test configurations (model + reasoning effort combinations)
   */
  readonly selectedCount = computed(() => {
    let count = 0;
    for (const model of this.modelSelections()) {
      if (!model.selected) continue;

      if (model.supportedReasoningEfforts.length > 0) {
        // Count each selected reasoning effort as a separate configuration
        count += model.selectedReasoningEfforts.length;
      } else {
        // Models without reasoning count as 1
        count += 1;
      }
    }
    return count;
  });

  runPreviews = signal<RunPreview[]>([]);

  private buildRunPreviews(): void {
    if (this.voiceOnly) return;
    const contextName =
      this.data?.assistant?.name ||
      this.translateService.instant('AI_ASSISTANT.TESTING.RUN_OPTIONS.RUN_NAME_FALLBACK');
    const previews: RunPreview[] = [];

    for (const model of this.modelSelections()) {
      if (!model.selected) continue;

      if (model.supportedReasoningEfforts.length > 0) {
        for (const effort of model.selectedReasoningEfforts) {
          previews.push({
            model: model.id,
            modelName: model.name,
            reasoningEffort: effort,
            name: `${contextName} - ${model.name} (${capitalizeFirst(effort)})`,
            isEditing: false,
          });
        }
      } else {
        previews.push({
          model: model.id,
          modelName: model.name,
          name: `${contextName} - ${model.name}`,
          isEditing: false,
        });
      }
    }

    this.runPreviews.set(previews);
  }

  updatePreviewName(preview: RunPreview, name: string): void {
    const previews = this.runPreviews();
    this.runPreviews.set(previews.map((p) => (p === preview ? { ...p, name } : p)));
  }

  toggleEditName(preview: RunPreview): void {
    const previews = this.runPreviews();
    this.runPreviews.set(previews.map((p) => (p === preview ? { ...p, isEditing: !p.isEditing } : p)));
  }

  toggleModel(model: ModelSelection): void {
    const selections = this.modelSelections();
    const updated = selections.map((m) => {
      if (m.id === model.id) {
        const newSelected = !m.selected;
        return {
          ...m,
          selected: newSelected,
          // When selecting a model with reasoning support, auto-select first effort if none selected
          selectedReasoningEfforts:
            newSelected && m.supportedReasoningEfforts.length > 0 && m.selectedReasoningEfforts.length === 0
              ? [m.supportedReasoningEfforts[0]]
              : m.selectedReasoningEfforts,
        };
      }
      return m;
    });
    this.modelSelections.set(updated);
    this.buildRunPreviews();
  }

  toggleReasoningEffort(model: ModelSelection, effort: string): void {
    const selections = this.modelSelections();
    const updated = selections.map((m) => {
      if (m.id === model.id) {
        const currentEfforts = m.selectedReasoningEfforts;
        const isSelected = currentEfforts.includes(effort);

        let newEfforts: string[];
        if (isSelected) {
          // Don't allow deselecting the last effort
          if (currentEfforts.length === 1) {
            return m;
          }
          newEfforts = currentEfforts.filter((e) => e !== effort);
        } else {
          newEfforts = [...currentEfforts, effort];
        }

        return { ...m, selectedReasoningEfforts: newEfforts };
      }
      return m;
    });
    this.modelSelections.set(updated);
    this.buildRunPreviews();
  }

  /**
   * Check if a reasoning effort is selected for a model
   */
  isReasoningEffortSelected(model: ModelSelection, effort: string): boolean {
    return model.selectedReasoningEfforts.includes(effort);
  }

  protected capitalizeFirst = capitalizeFirst;

  updateIterations(event: Event): void {
    const value = (event.target as HTMLInputElement).valueAsNumber;
    this.iterations.set(clampIterations(value));
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.voiceOnly) {
      this.dialogRef.close({
        llmOptions: [],
        voiceConfigs: this.voiceConfigEntries().map((e) => e.form.toAssistantVoiceConfig()),
        iterations: this.iterations(),
        runNames: this.runPreviews().map((p) => p.name),
      });
      return;
    }

    const llmOptions: LLMOptionPair[] = [];

    for (const model of this.modelSelections()) {
      if (!model.selected) continue;

      if (model.supportedReasoningEfforts.length > 0) {
        for (const effort of model.selectedReasoningEfforts) {
          llmOptions.push({
            model: model.id || undefined,
            reasoningEffort: effort,
          });
        }
      } else {
        llmOptions.push({
          model: model.id || undefined,
          reasoningEffort: undefined,
        });
      }
    }

    const result: TestRunOptionsModalResult = {
      llmOptions,
      voiceConfigs: [],
      iterations: this.iterations(),
      runNames: this.runPreviews().map((p) => p.name),
    };

    this.dialogRef.close(result);
  }
}
